import React from 'react';
import {
  ChipField,
  Loading,
  RecordContextProvider,
  SingleFieldList,
  useGetOne,
  useRecordContext,
} from 'react-admin';
import { Grid } from '@mui/material';

import auditTrackDiffGenerator, { AuditTrack } from '../audit/auditDiffGenerator/auditTrackDiffGenerator';
import AuditExtraInfo from '../audit/AuditExtraInfo';
import ChipStringField from '../../customFields/ChipStringField';
import NoResults from '../layout/NoResults';
import TextDiffField from '../utils/auditTracks/TextDiffField';
import ArrayDiffField from '../utils/auditTracks/ArrayDiffField';
import DateDiffField from '../utils/auditTracks/DateDiffField';
import StatusDiffField from '../utils/auditTracks/StatusDiffField';
import ReferenceArrayDiffField from '../utils/auditTracks/ReferenceArrayDiffField';
import resourceNames from '../../constants/resourceNames';
import { ICar } from '../../utilities/schemas/car';

const CarAuditDetails = () => {
  const record = useRecordContext<AuditTrack<ICar>>();
  const { data, isLoading } = useGetOne<AuditTrack<ICar>>(
    `audit-tracks/${record?.id}/previous`,
    { id: '' },
    { enabled: !!record },
  );

  if (!record) return <NoResults />;

  if (!data) {
    if (isLoading) return <Loading />;
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const isElementCreated = data.id === record.id;

  if (!record.documentAfter || (!isElementCreated && !data.documentAfter)) {
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const auditTracksDiff = auditTrackDiffGenerator<ICar>({
    current: record,
    previous: !isElementCreated ? data : undefined,
  });

  const { resourceData } = auditTracksDiff;

  const hasFieldChanges = {
    id: resourceData.id?.hasChanged,
    accountId: resourceData.accountId?.hasChanged,
    ownerId: resourceData.owner?.id?.hasChanged,
    ownerType: resourceData.owner?.type?.hasChanged,
    customIds: resourceData.customIds?.hasChanged,
    registrationNumber: resourceData.registrationNumber?.hasChanged,
    registrationCountry: resourceData.registrationCountry?.hasChanged,
    vinCode: resourceData.vinCode?.hasChanged,
    carModel: resourceData.carModel?.hasChanged,
    createdAt: resourceData.createdAt?.hasChanged,
    status: resourceData.status?.hasChanged,
    riskScore: resourceData.riskScore?.hasChanged,
    scoreLatestCalculationDate: resourceData.scoreLatestCalculationDate?.hasChanged,
    kycApprovedAt: resourceData.kycApprovedAt?.hasChanged,
    currencies: resourceData.currencies?.hasChanged,
    groups: resourceData.groups?.hasChanged,
  };

  const hasAnyChanges = Object.values(hasFieldChanges).some((field) => field);

  return (
    <Grid container spacing={6}>
      {!hasAnyChanges && <NoResults text="Only system changes detected" />}
      {hasAnyChanges && (
        <RecordContextProvider value={resourceData}>
          {hasFieldChanges.id && <TextDiffField source="id" label="ID" />}

          {hasFieldChanges.accountId && <TextDiffField source="accountId" label="Account ID" />}

          {hasFieldChanges.ownerId && <TextDiffField source="owner.id" label="Owner ID" />}
          {hasFieldChanges.ownerType && <TextDiffField source="owner.type" label="Owner Type" />}

          {hasFieldChanges.customIds && (
            <ArrayDiffField source="customIds" label="Custom IDs">
              <SingleFieldList linkType={false}>
                <ChipStringField />
              </SingleFieldList>
            </ArrayDiffField>
          )}

          {hasFieldChanges.registrationNumber && (
            <TextDiffField source="registrationNumber" label="Registration number" />
          )}

          {hasFieldChanges.registrationCountry && (
            <TextDiffField source="registrationCountry" label="Registration country" />
          )}

          {hasFieldChanges.vinCode && <TextDiffField source="vinCode" label="VIN code" />}

          {hasFieldChanges.carModel && <TextDiffField source="carModel" label="Car model" />}

          {hasFieldChanges.createdAt && <DateDiffField source="createdAt" label="Created at" showTime />}

          {hasFieldChanges.status && <StatusDiffField source="status" label="Status" />}

          {hasFieldChanges.riskScore && <TextDiffField source="riskScore" label="Risk score" />}

          {hasFieldChanges.scoreLatestCalculationDate && (
            <DateDiffField source="scoreLatestCalculationDate" label="Score latest calculation date" />
          )}

          {hasFieldChanges.kycApprovedAt && (
            <DateDiffField source="kycApprovedAt" label="KYC approved at" />
          )}

          {hasFieldChanges.currencies && (
            <ArrayDiffField source="currencies" label="Currencies">
              <SingleFieldList linkType={false}>
                <ChipStringField />
              </SingleFieldList>
            </ArrayDiffField>
          )}

          {hasFieldChanges.groups && (
            <ReferenceArrayDiffField
              source="groups"
              label="Groups"
              reference={resourceNames.GROUPS}
            >
              <SingleFieldList linkType={false}>
                <ChipField source="name" size="small" />
              </SingleFieldList>
            </ReferenceArrayDiffField>
          )}
        </RecordContextProvider>
      )}
      {record.extraInfo && <AuditExtraInfo />}
    </Grid>
  );
};

export default CarAuditDetails;
